// ─────────────────────────────────────────────
//  Firestore primitive barrel — real SDK, or the sandbox shim.
//
//  WHY THIS FILE EXISTS, because the obvious alternative does not work.
//
//  The sandbox was designed to swap only `db` in config.js, on the theory that
//  every helper in firestore.js would then operate on a local store unchanged.
//  It does not. The real SDK type-checks its first argument:
//
//    collection(sandboxDb, "rooms")
//      → "Expected first argument to collection() to be a CollectionReference,
//         a DocumentReference or FirebaseFirestore"
//
//  So swapping the database object while still calling the REAL primitives
//  means every call throws on its first use. The primitives have to be swapped
//  too — which is what this barrel does.
//
//  The choice is made ONCE, at module load, from the same boot flag config.js
//  reads. It cannot change at runtime: `db` is bound at boot, and a mid-session
//  switch would leave live onSnapshot subscriptions attached to the previous
//  store, failing silently rather than loudly. Entering or leaving the sandbox
//  reloads the page.
//
//  EVERY module that calls a Firestore primitive against the exported `db`
//  imports from here instead of from "firebase/firestore". There are exactly
//  two deliberate exceptions, and both are correct:
//
//    • src/firebase/config.js  — builds the real Firestore instance. It must
//      use the real initializeFirestore/getFirestore or there would be nothing
//      to swap in the first place.
//    • src/sandbox/orgRequest.js — the one intentional bypass. An organization
//      request has to reach the REAL database even while the app is in sandbox
//      mode, so it takes its own getFirestore(app) handle.
//
//  If you add a new module that talks to Firestore, import from this file. A
//  raw "firebase/firestore" import will work perfectly in normal mode and fail
//  only in the sandbox, which is the worst way to find out.
// ─────────────────────────────────────────────
import * as real from "firebase/firestore";
import * as local from "../sandbox/localDb";
import { isSandboxBoot } from "../sandbox/mode";
import { isTestModeBoot } from "../domain/testMode";

// isSandboxBoot() is total — it swallows a hostile localStorage rather than
// throwing — because this module is evaluated at import time, and a throw at
// module scope kills the module graph before React mounts. That failure mode
// has already cost this project two blank-screen releases on iOS.
// ⚠️ EVERY LOCAL-STORE BOOT, NOT JUST THE SANDBOX. config.js swaps `db` for a
// local store in TWO cases — the sandbox and testing mode — and this line only
// asked about the first. The result was the one combination this whole file
// exists to prevent: a LOCAL database driven by the REAL SDK primitives. The
// real SDK type-checks its first argument, so `listenToUser` threw inside the
// onAuth callback, `setLoading(false)` was never reached, and testing mode hung
// on AuthContext's BootScreen forever. Because the app never rendered, the
// amber banner never rendered either — and its "Exit preview" button is the
// only way out, so entering testing mode TRAPPED the admin until storage was
// cleared by hand. Shipped broken; caught in live QA 2026-09-07.
//
// The predicate is "is `db` local?", which is exactly config.js's own
// condition. A fourth boot mode must extend BOTH or neither — see the test in
// sdk.bootmode.test.js, which pins them together.
const impl = isSandboxBoot() || isTestModeBoot() ? local : real;

// Named re-exports rather than `export *`, so that a primitive the shim has not
// implemented is a build-time error here instead of an `undefined is not a
// function` on a screen nobody opened during testing.
export const collection      = impl.collection;
export const doc             = impl.doc;
export const getDoc          = impl.getDoc;
export const getDocs         = impl.getDocs;
export const setDoc          = impl.setDoc;
export const addDoc          = impl.addDoc;
export const updateDoc       = impl.updateDoc;
export const deleteDoc       = impl.deleteDoc;
export const onSnapshot      = impl.onSnapshot;
export const query           = impl.query;
export const where           = impl.where;
export const orderBy         = impl.orderBy;
export const limit           = impl.limit;
export const serverTimestamp = impl.serverTimestamp;
export const Timestamp       = impl.Timestamp;
export const arrayUnion      = impl.arrayUnion;
export const arrayRemove     = impl.arrayRemove;
export const deleteField     = impl.deleteField;
export const writeBatch      = impl.writeBatch;
export const runTransaction  = impl.runTransaction;
